if (!customElements.get('og-custom-widget')) {
  class OgCustomWidget extends HTMLElement {
    constructor () {
      super()
      this.selectedVariant = null
      this.selectedVariantChewCount = 0
      this.selectedVariantCompareAtPrice = null
      this.selectedVariantPrice = null
      this.selectedVariantOneTimePrice = null
      this.selectedChewsPerDay = 0
      this.recommendedSellingPlan = null
      this._pricingUpdateTimer = null
      this._pricingRetryCount = 0
      this.handleVariantChange = this.handleVariantChange.bind(this)
      this.handleFrequencyChange = this.handleFrequencyChange.bind(this)
      this.handleSubscribeChange = this.handleSubscribeChange.bind(this)
      this.handleCustomButtonClick = this.handleCustomButtonClick.bind(this)
      this.handleVariantChangedEvent = this.handleVariantChangedEvent.bind(this)
      this.handleProductRerender = this.handleProductRerender.bind(this)
    }

    resolveBlockContainer () {
      const ogOfferEl = this.getRootNode()?.host
      return ogOfferEl?.closest('[data-block-type="custom-ordergroove-widget"]') ?? null
    }

    resolveProductRoot () {
      const blockContainer = this.blockContainer
      if (blockContainer?.closest) {
        return blockContainer.closest('.product')
          || blockContainer.closest('product-rerender')?.querySelector('.product')
          || null
      }
      return document.querySelector('.shopify-section--main-product .product')
        || document.querySelector('product-rerender .product')
        || null
    }

    connectedCallback () {
      this._disconnected = false

      this.parentWrapper = this.closest('og-offer')

      /* --- Scoped Container ---
       * og-custom-widget lives in og-offer's shadow DOM. Walk up via the shadow
       * root host to find the light-DOM block container, so queries for
       * #og-custom-buttons and [data-variant-metafields] are scoped to the
       * correct product instead of grabbing the first match on the page.
       */
      this.blockContainer = this.resolveBlockContainer()
      const blockScope = this.blockContainer ?? document

      /* --- Elements & Data --- */

      // Custom Buttons
      const documentCustomButtonsEl = blockScope.querySelector('#og-custom-buttons')
      const customButtons = documentCustomButtonsEl ? documentCustomButtonsEl.content.cloneNode(true) : null
      this.customButtonsContainer = this.querySelector('og-custom-buttons')
      if (this.customButtonsContainer && customButtons) {
        this.customButtonsContainer.appendChild(customButtons)
      }

      // Variant Metafields that Map Variant IDs to Chew Counts
      const variantMetaFieldMapEl = blockScope.querySelector('[data-variant-metafields]')
      this.variantMetaFieldMap = variantMetaFieldMapEl ? JSON.parse(variantMetaFieldMapEl.textContent) : {}

      // Subscribe Checkbox & Opt-in/out Buttons
      this.subscribeCheckbox = this.querySelector('#og-subscribe-checkbox')
      this.optOutButton = this.querySelector('og-optout-button')
      this.optInButton = this.querySelector('og-optin-button')

      // Pricing Elements
      this.productRoot = this.resolveProductRoot()
      this.productInfoEl = document.querySelector('.product__info')
      this.regularPriceEl = this.querySelector('og-price[regular]')
      this.subscriptionPriceEl = this.querySelector('og-price[subscription]')

      /* --- Event Listeners --- */

      // Listen for user interaction with our custom checkbox
      if (this.subscribeCheckbox) {
        this.subscribeCheckbox.addEventListener('change', this.handleSubscribeChange)
      }

      // Listen for clicks on our custom buttons
      if (this.customButtonsContainer) {
        this.customButtonsContainer.addEventListener('click', this.handleCustomButtonClick)
      }

      // Observe the opt-in button for changes to the 'subscribed' attribute so we can keep our checkbox in sync
      this.observeWidgetUpdates()
      this.bindThemeVariantEvents()

      // On load, check the URL for a variant ID and set variant data accordingly
      this.setVariantIDFromURL()
      if (!this.selectedVariant) {
        const initialVariantID = this.parentWrapper?.getAttribute('product')
        if (initialVariantID) {
          this.selectedVariant = initialVariantID
          this.setVariantDataFromID()
        }
      }

      // The frequency select lives inside nested shadow DOMs (og-select-frequency > og-select > select)
      // that OG populates asynchronously after connectedCallback fires. Poll until ready.
      this.initFrequencySelect()
    }

    disconnectedCallback () {
      this._disconnected = true
      this.subscribeCheckbox?.removeEventListener('change', this.handleSubscribeChange)
      this.customButtonsContainer?.removeEventListener('click', this.handleCustomButtonClick)
      this.frequencySelect?.removeEventListener('change', this.handleFrequencyChange)
      this.productForm?.removeEventListener('variant:changed', this.handleVariantChangedEvent)
      this.productForm?.removeEventListener('product:rerender', this.handleProductRerender)
      clearTimeout(this._pricingUpdateTimer)
      this.widgetObserver?.disconnect()
      this.priceObserver?.disconnect()
      this.regularPriceObserver?.disconnect()
      this.widgetObserver = null
      this.priceObserver = null
      this.regularPriceObserver = null
    }

    bindThemeVariantEvents () {
      const productRoot = this.productRoot || this.resolveProductRoot()
      const variantPicker = productRoot?.querySelector('variant-picker[form-id]')
      const formId = variantPicker?.getAttribute('form-id')
      this.productForm = formId
        ? document.getElementById(formId)
        : (this.blockContainer?.closest?.('form') ?? productRoot?.querySelector('form'))

      if (!this.productForm) {
        return
      }

      this.productForm.addEventListener('variant:changed', this.handleVariantChangedEvent)
      this.productForm.addEventListener('product:rerender', this.handleProductRerender)
    }

    handleVariantChangedEvent (event) {
      const variant = event.detail?.variant
      if (!variant) return
      this.applyVariantFromShopify(variant)
      // product:rerender follows variant:changed; refresh after Focal swaps product-meta
      this.schedulePricingUpdate(100)
    }

    handleProductRerender () {
      this.refreshPricingElements()
      this.schedulePricingUpdate(100)
    }

    applyVariantFromShopify (variant) {
      this.selectedVariant = String(variant.id)
      this.setVariantDataFromID()

      if (variant.price != null) {
        this.selectedVariantPrice = variant.price / 100
      }
      const compareAt = variant.compare_at_price
      this.selectedVariantCompareAtPrice = (compareAt != null && compareAt > 0) ? compareAt / 100 : null
    }

    refreshPricingElements () {
      this.blockContainer = this.resolveBlockContainer()
      this.productRoot = this.resolveProductRoot()
      this.productInfoEl = document.querySelector('.product__info')
      this.regularPriceEl = this.querySelector('og-price[regular]')
      this.subscriptionPriceEl = this.querySelector('og-price[subscription]')
      this.reconnectPriceObservers()
    }

    resolveActiveBadgesContainer () {
      const root = (this.productRoot && document.contains(this.productRoot))
        ? this.productRoot
        : this.resolveProductRoot()
      if (!root) return null

      const gallery = root.querySelector('[data-product-badges].label-list--image-gallery')
      if (gallery) return gallery

      return root.querySelector('[data-product-badges].label-list--price-area') ||
        root.querySelector('[data-product-badges]')
    }

    getPriceSavingsEls () {
      const container = this.resolveActiveBadgesContainer()
      if (!container) return []

      const el = container.querySelector('[data-price-savings]')
      return el && document.contains(el) ? [el] : []
    }

    resolvePriceSavingsEls () {
      let els = this.getPriceSavingsEls()
      if (!els.length) {
        this.ensurePriceSavingsEl()
        els = this.getPriceSavingsEls()
      }
      return els
    }

    reconnectPriceObservers () {
      this.priceObserver?.disconnect()
      this.regularPriceObserver?.disconnect()

      this.priceObserver = new MutationObserver(() => this.schedulePricingUpdate())
      if (this.subscriptionPriceEl?.shadowRoot) {
        this.priceObserver.observe(this.subscriptionPriceEl.shadowRoot, {
          childList: true,
          characterData: true,
          subtree: true,
        })
      }

      this.regularPriceObserver = new MutationObserver(() => this.schedulePricingUpdate())
      if (this.regularPriceEl?.shadowRoot) {
        this.regularPriceObserver.observe(this.regularPriceEl.shadowRoot, {
          childList: true,
          characterData: true,
          subtree: true,
        })
      }
    }

    schedulePricingUpdate (delay = 50) {
      clearTimeout(this._pricingUpdateTimer)
      this._pricingUpdateTimer = setTimeout(() => {
        if (this._disconnected) return
        this.updatePricing()
      }, delay)
    }

    deferPricingUntilOgReady () {
      this._pricingRetryCount += 1
      if (this._pricingRetryCount > 20) {
        return
      }
      this.schedulePricingUpdate(100)
    }

    initFrequencySelect () {
      let attempts = 0
      const maxAttempts = 40 // ~2s at rAF rate (~50ms each)

      const tryInit = () => {
        if (this._disconnected) return

        const frequencySelectorEl = this.querySelector('og-select-frequency')
        const frequencySelectEl = frequencySelectorEl?.shadowRoot?.querySelector('og-select')
        const select = frequencySelectEl?.shadowRoot?.querySelector('select')

        if (select) {
          this.frequencySelect = select
          frequencySelectEl.style.width = '100%' // Fill entire width inside shadow root
          this.frequencySelect.addEventListener('change', this.handleFrequencyChange)
          return
        }

        if (++attempts < maxAttempts) {
          requestAnimationFrame(tryInit)
        }
      }

      requestAnimationFrame(tryInit)
    }

    setVariantIDFromURL () {
      const urlParams = new URLSearchParams(window.location.search)
      const variantID = urlParams.get('variant')
      if (variantID) {
        this.selectedVariant = variantID
        this.setVariantDataFromID()
      }
    }

    setVariantDataFromID () {
      const entry = Object.values(this.variantMetaFieldMap).length
        ? this.variantMetaFieldMap[this.selectedVariant] ?? Object.values(this.variantMetaFieldMap)[0]
        : null

      const fallback = Object.values(this.variantMetaFieldMap)[0]

      this.selectedVariantChewCount = entry?.['chews_per_package'] ?? fallback?.['chews_per_package'] ?? 0

      const rawPrice = entry?.['price'] ?? 0
      this.selectedVariantPrice = rawPrice > 0 ? rawPrice / 100 : null

      const rawCompareAt = entry?.['compare_at_price'] ?? 0
      this.selectedVariantCompareAtPrice = rawCompareAt > 0 ? rawCompareAt / 100 : null

      // PDP one-time price override (variant.one_time_price metafield). When set, the
      // one-time display uses this instead of the variant price, with no compare-at.
      const rawOneTime = entry?.['one_time_price'] ?? 0
      this.selectedVariantOneTimePrice = rawOneTime > 0 ? rawOneTime / 100 : null
    }

    handleSubscribeChange (event) {
      if (event.target.checked) {
        this.optInButton?.click()
        this.reHydrateButtonStates()
      } else {
        this.optOutButton?.click()
      }
      this.schedulePricingUpdate(150)
    }

    handleVariantChange (variantID) {
      if (!variantID || String(variantID) === String(this.selectedVariant)) return
      this.selectedVariant = variantID
      this.setVariantDataFromID()
      this.reHydrateButtonStates()
      this.schedulePricingUpdate(150)
    }

    handleFrequencyChange (event) {
      // Our custom buttons include the sellingPlanID when updating frequency, if it's missing, reset the button states.
      if (!event.detail?.sellingPlanID) this.resetButtonStates()
    }

    handleCustomButtonClick (event) {
      if (!this.customButtonsContainer) return
      const button = event.target.closest('button[data-chews-per-day]')
      if (button) {
        this.selectedChewsPerDay = parseInt(button.getAttribute('data-chews-per-day'), 10)
        if (!Number.isFinite(this.selectedChewsPerDay) || this.selectedChewsPerDay <= 0) {
          return
        }
        this.customButtonsContainer.querySelectorAll('button[data-chews-per-day]').forEach(btn => {
          btn.setAttribute('aria-pressed', 'false')
        })
        button.setAttribute('aria-pressed', 'true')

        // Loop through the frequency options and find the best one based on how long the chews will last
        const daysChewsLast = Math.ceil(this.selectedVariantChewCount / this.selectedChewsPerDay)
        if (this.frequencySelect) {
          const options = this.frequencySelect.querySelectorAll('option')
          const optionsSortedByDays = Array.from(options).sort((a, b) => {
            const aDays = parseInt(a?.querySelector('og-text')?.getAttribute('pluralize') || '0')
            const bDays = parseInt(b?.querySelector('og-text')?.getAttribute('pluralize') || '0')
            return aDays - bDays
          })
          this.recommendedSellingPlan = optionsSortedByDays[0]?.value
          optionsSortedByDays.forEach(option => {
            const sellingPlanID = option.value
            const daysBetweenDeliveries = option?.querySelector('og-text')?.getAttribute('pluralize')

            if (sellingPlanID && daysBetweenDeliveries) {
              const daysBetween = parseInt(daysBetweenDeliveries)
              if (daysChewsLast >= daysBetween) {
                this.recommendedSellingPlan = sellingPlanID
              }
            }
          })
          this.frequencySelect.value = this.recommendedSellingPlan
          this.frequencySelect.dispatchEvent(new CustomEvent('change', {
            bubbles: true,
            detail: { sellingPlanID: this.frequencySelect.value }
          }))
        }
      }
    }

    updatePricing () {
      if (!this.productInfoEl || !document.contains(this.productInfoEl)) {
        this.productInfoEl = document.querySelector('.product__info')
      }
      if (!this.productInfoEl) {
        return
      }

      const priceListEl = this.productInfoEl.querySelector('product-meta .price-list')
      if (!priceListEl) {
        return
      }

      const oneTimePrice = this.getOneTimeVariantPrice()
      const oneTimeDisplayPrice = this.getOneTimeDisplayPrice()
      const hasOneTimeOverride = this.hasOneTimeOverride()
      const regularPriceValue = this.parsePriceValue(this.regularPriceEl)
      const subscriptionPriceValue = this.parsePriceValue(this.subscriptionPriceEl)
      const compareAtPrice = this.selectedVariantCompareAtPrice ?? oneTimePrice ?? regularPriceValue
      const subscribed = this.isSubscribed()

      if (subscribed && !Number.isFinite(subscriptionPriceValue)) {
        this.deferPricingUntilOgReady()
        return
      }

      this._pricingRetryCount = 0

      if (subscribed && Number.isFinite(subscriptionPriceValue)) {
        const strikePrice = Number.isFinite(compareAtPrice) ? compareAtPrice : oneTimePrice
        if (!Number.isFinite(strikePrice)) {
          this.deferPricingUntilOgReady()
          return
        }
        priceListEl.innerHTML = `
          <span class="price price--highlight">
            <span class="visually-hidden">Sale price</span>
            $${subscriptionPriceValue.toFixed(2)}
          </span>
          <span class="price price--compare">
            <span class="visually-hidden">Regular price</span>
            $${strikePrice.toFixed(2)}
          </span>
        `
      } else if (!subscribed) {
        const salePrice = Number.isFinite(oneTimeDisplayPrice) ? oneTimeDisplayPrice : regularPriceValue
        const compareAt = this.selectedVariantCompareAtPrice

        if (hasOneTimeOverride && Number.isFinite(salePrice)) {
          // One-time price override: show the metafield price only, no compare-at strikethrough.
          priceListEl.innerHTML = `
            <span class="price price--highlight">
              <span class="visually-hidden">Sale price</span>
              $${salePrice.toFixed(2)}
            </span>
          `
        } else if (Number.isFinite(salePrice) && Number.isFinite(compareAt) && compareAt > salePrice) {
          priceListEl.innerHTML = `
            <span class="price price--highlight">
              <span class="visually-hidden">Sale price</span>
              $${salePrice.toFixed(2)}
            </span>
            <span class="price price--compare">
              <span class="visually-hidden">Regular price</span>
              $${compareAt.toFixed(2)}
            </span>
          `
        } else if (Number.isFinite(salePrice)) {
          priceListEl.innerHTML = `
            <span class="price">
              <span class="visually-hidden">Regular price</span>
              $${salePrice.toFixed(2)}
            </span>
          `
        }
      }

      this.updatePriceSavings()
      this.updatePricePerUnit()
    }

    ensurePriceSavingsEl () {
      const container = this.resolveActiveBadgesContainer()
      if (!container || container.querySelector('[data-price-savings]')) return

      const el = document.createElement('span')
      el.className = 'price-savings label label--highlight'
      el.setAttribute('data-price-savings', '')
      el.setAttribute('hidden', '')
      el.style.display = 'none'
      container.appendChild(el)
    }

    setPriceSavingsVisible (visible, text = '') {
      const els = this.resolvePriceSavingsEls()
      if (!els.length) return

      els.forEach(el => {
        if (visible) {
          el.textContent = text
          el.removeAttribute('hidden')
          el.style.display = ''
        } else {
          el.setAttribute('hidden', '')
          // .label sets display:inline-block and overrides the [hidden] rule in theme.css
          el.style.display = 'none'
        }
      })

      this.syncImageGalleryBadgeVisibility()
    }

    syncImageGalleryBadgeVisibility () {
      const root = this.productRoot || this.resolveProductRoot()
      if (!root) return

      root.querySelectorAll('[data-product-badges].label-list--image-gallery[data-state="empty"]').forEach(container => {
        const savings = container.querySelector('[data-price-savings]')
        const show = savings && !savings.hasAttribute('hidden') && savings.style.display !== 'none'
        container.style.display = show ? '' : 'none'
      })
    }

    isSubscribed () {
      if (this.subscribeCheckbox) return this.subscribeCheckbox.checked
      return this.parentWrapper?.hasAttribute('subscribed') ?? false
    }

    getOneTimeVariantPrice () {
      if (Number.isFinite(this.selectedVariantPrice) && this.selectedVariantPrice > 0) {
        return this.selectedVariantPrice
      }
      return this.parsePriceValue(this.regularPriceEl)
    }

    hasOneTimeOverride () {
      return Number.isFinite(this.selectedVariantOneTimePrice) && this.selectedVariantOneTimePrice > 0
    }

    // Price shown in the one-time state: the metafield override when present, else the regular variant price.
    getOneTimeDisplayPrice () {
      if (this.hasOneTimeOverride()) {
        return this.selectedVariantOneTimePrice
      }
      return this.getOneTimeVariantPrice()
    }

    updatePriceSavings () {
      if (!this.resolvePriceSavingsEls().length) {
        return
      }

      const oneTimePrice = this.getOneTimeVariantPrice()
      const subscriptionPriceValue = this.parsePriceValue(this.subscriptionPriceEl)
      const subscribed = this.isSubscribed()

      if (subscribed && !Number.isFinite(subscriptionPriceValue)) {
        this.deferPricingUntilOgReady()
        return
      }

      const compareAtPrice = this.selectedVariantCompareAtPrice
      const savingsBaseline =
        Number.isFinite(compareAtPrice) && compareAtPrice > 0 ? compareAtPrice : oneTimePrice

      if (
        subscribed &&
        Number.isFinite(subscriptionPriceValue) &&
        Number.isFinite(savingsBaseline) &&
        savingsBaseline > 0 &&
        subscriptionPriceValue < savingsBaseline
      ) {
        const percentOff = Math.round((savingsBaseline - subscriptionPriceValue) / savingsBaseline * 100)
        if (percentOff > 0) {
          this.setPriceSavingsVisible(true, `Save ${percentOff}%`)
          return
        }
      }

      if (!subscribed && this.hasOneTimeOverride()) {
        // One-time price override: savings is the regular variant price minus the metafield price.
        const oneTimeDisplayPrice = this.getOneTimeDisplayPrice()
        if (Number.isFinite(oneTimePrice) && Number.isFinite(oneTimeDisplayPrice) && oneTimePrice > oneTimeDisplayPrice) {
          const dollarSavings = oneTimePrice - oneTimeDisplayPrice
          this.setPriceSavingsVisible(true, `Save $${dollarSavings.toFixed(2)}`)
          return
        }
        this.setPriceSavingsVisible(false)
        return
      }

      if (!subscribed && Number.isFinite(oneTimePrice) && Number.isFinite(compareAtPrice) && compareAtPrice > oneTimePrice) {
        const dollarSavings = compareAtPrice - oneTimePrice
        this.setPriceSavingsVisible(true, `Save $${dollarSavings.toFixed(2)}`)
        return
      }

      this.setPriceSavingsVisible(false)
    }

    updatePricePerUnit () {
      const pricePerUnitEls = document.querySelectorAll('.price-per-package')
      if (!pricePerUnitEls.length) return

      const regularPriceValue = this.parsePriceValue(this.regularPriceEl)
      const subscriptionPriceValue = this.parsePriceValue(this.subscriptionPriceEl)
      const subscribed = this.isSubscribed() && Number.isFinite(subscriptionPriceValue) && Number.isFinite(regularPriceValue) && regularPriceValue > 0
      const discountRatio = subscribed ? subscriptionPriceValue / regularPriceValue : 1

      pricePerUnitEls.forEach(el => {
        if (el.hasAttribute('data-no-subscription')) return
        const regularPrice = parseFloat(el.getAttribute('data-regular-price'))
        const unitsPerPackage = parseInt(el.getAttribute('data-units-per-package'), 10)
        if (!Number.isFinite(regularPrice) || !Number.isFinite(unitsPerPackage) || unitsPerPackage <= 0) return
        el.textContent = `$${(regularPrice * discountRatio / unitsPerPackage).toFixed(2)}/ea`
      })
    }

    parsePriceValue (priceEl) {
      const rawText = priceEl?.shadowRoot?.textContent?.trim()
      if (!rawText) return NaN
      return parseFloat(rawText.replace(/[^0-9.]/g, ''))
    }

    reHydrateButtonStates () {
      if (!this.customButtonsContainer) return
      const button = this.customButtonsContainer.querySelector('button[data-chews-per-day][aria-pressed="true"]')
      setTimeout(() => {button?.click()}, 0)
    }

    resetButtonStates () {
      if (!this.customButtonsContainer) return
      const buttons = this.customButtonsContainer.querySelectorAll('button[data-chews-per-day]')
      buttons.forEach(btn => btn.setAttribute('aria-pressed', 'false'))
    }

    syncCheckboxToSubscribed () {
      if (!this.subscribeCheckbox) return
      this.subscribeCheckbox.checked = this.parentWrapper.hasAttribute('subscribed')
      this._pricingRetryCount = 0
      this.schedulePricingUpdate(this.subscribeCheckbox.checked ? 150 : 50)
    }

    observeWidgetUpdates () {
      if (!this.parentWrapper) {
        return
      }

      this.widgetObserver = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes' && m.attributeName === 'subscribed') {
            this.refreshPricingElements()
            this.syncCheckboxToSubscribed()
          }
          if (m.type === 'attributes' && m.attributeName === 'product') {
            const newVariantID = m.target.getAttribute('product')
            if (String(newVariantID) !== String(this.selectedVariant)) {
              this.handleVariantChange(newVariantID)
            }
          }
        }
      })

      this.widgetObserver.observe(this.parentWrapper, {
        attributes: true,
        attributeFilter: ['subscribed', 'product'],
      })

      this.reconnectPriceObservers()

      this.syncCheckboxToSubscribed()
    }
  }

  customElements.define('og-custom-widget', OgCustomWidget)
}
